import { loadReportOneDSEvent } from '@ms/yammer-telemetry-1ds';
import {
  bufferPreConfigEvent,
  getTelemetryClientConfig,
  isTelemetryClientConfigConfigured,
} from '@ms/yammer-telemetry-store';

import type { AnalyticsEvent, AnalyticsV2Event, LogEvent, TelemetryEvent } from '../events';
import { isAnalyticsEvent, isAnalyticsV2Event, isLogEvent } from '../events';
import { applyMiddleware } from '../middleware';

import { reportAnalyticsEvent, reportAnalyticsEventNow } from './reportAnalytics';
import { reportAnalyticsV2Event } from './reportAnalyticsV2';
import { reportLogEvent, reportLogEventNow } from './reportLog';

export type OneDSEventPayload = Parameters<Awaited<ReturnType<typeof loadReportOneDSEvent>>['reportOneDSEvent']>[0];

const isOneDSDestination = (): boolean => {
  const config = getTelemetryClientConfig();

  return !!config?.oneDSInstrumentationKey;
};

const sendTo1DS = async (payload: OneDSEventPayload): Promise<void> => {
  const { reportOneDSEvent } = await loadReportOneDSEvent();
  reportOneDSEvent(payload);
};

const emitToLegacy = async (event: TelemetryEvent): Promise<void> => {
  const processedEvent = await applyMiddleware(event);

  if (isAnalyticsEvent(processedEvent)) {
    reportAnalyticsEvent(processedEvent);
  } else if (isAnalyticsV2Event(processedEvent)) {
    reportAnalyticsV2Event(processedEvent);
  } else {
    reportLogEvent(processedEvent);
  }
};

const emitToLegacyNow = async (event: AnalyticsEvent | LogEvent): Promise<void> => {
  const processedEvent = await applyMiddleware(event);

  if (isAnalyticsEvent(processedEvent)) {
    await reportAnalyticsEventNow(processedEvent);
  } else if (isLogEvent(processedEvent)) {
    await reportLogEventNow(processedEvent);
  }
};

export type EmitTelemetryEvent = (event: TelemetryEvent, oneDSPayload?: OneDSEventPayload) => Promise<void>;
export const emitTelemetryEvent: EmitTelemetryEvent = async (event, oneDSPayload) => {
  if (oneDSPayload && isOneDSDestination()) {
    await sendTo1DS(oneDSPayload);

    return;
  }

  if (!isTelemetryClientConfigConfigured()) {
    bufferPreConfigEvent(event);

    return;
  }

  await emitToLegacy(event);
};

export type EmitTelemetryEventNow = (
  event: AnalyticsEvent | LogEvent,
  oneDSPayload?: OneDSEventPayload
) => Promise<void>;
export const emitTelemetryEventNow: EmitTelemetryEventNow = async (event, oneDSPayload) => {
  if (oneDSPayload && isOneDSDestination()) {
    await sendTo1DS(oneDSPayload);

    return;
  }

  if (!isTelemetryClientConfigConfigured()) {
    bufferPreConfigEvent(event);

    return;
  }

  await emitToLegacyNow(event);
};

export interface EmitAnalyticsV2TelemetryEventOptions {
  readonly oneDSPayload: OneDSEventPayload;
  readonly getLegacyEvent: () => Promise<AnalyticsV2Event | undefined>;
}

export type EmitAnalyticsV2TelemetryEvent = (options: EmitAnalyticsV2TelemetryEventOptions) => Promise<void>;
export const emitAnalyticsV2TelemetryEvent: EmitAnalyticsV2TelemetryEvent = async ({
  oneDSPayload,
  getLegacyEvent,
}) => {
  if (isOneDSDestination()) {
    await sendTo1DS(oneDSPayload);

    return;
  }

  const legacyEvent = await getLegacyEvent();
  if (legacyEvent) {
    await emitTelemetryEvent(legacyEvent);
  }
};
